import { cs } from '../../lib/currency';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Users,
  Settings,
  Grid3X3,
  List,
  RefreshCw,
  CreditCard,
  DollarSign,
  Loader2,
  Ban,
  CalendarClock,
  Pencil,
  Link2 as LinkIcon,
  Package,
  RotateCcw,
  X,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import { useFeature } from '../../hooks/useFeature';
import EditSeriesDialog from '../../components/admin/EditSeriesDialog';
import RefundDialog from '../../components/admin/RefundDialog';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar as CalendarPicker } from '../../components/ui/calendar';

// Split components — see /components/admin/lane/*
import BlockLaneModal from '../../components/admin/lane/BlockLaneModal';
import AdminBookingModal from '../../components/admin/lane/AdminBookingModal';
import SlotModal from '../../components/admin/lane/SlotModal';
import CreateSmallGroupModal from '../../components/admin/lane/CreateSmallGroupModal';
import RegisterPlayersDialog from '../../components/admin/lane/RegisterPlayersDialog';
import PaymentActionsModal from '../../components/admin/lane/PaymentActionsModal';
import BookingDetailsModal from '../../components/admin/lane/BookingDetailsModal';
import RescheduleModal from '../../components/admin/lane/RescheduleModal';
import CalendarGrid from '../../components/admin/lane/CalendarGrid';
import WaitlistPanel from '../../components/admin/lane/WaitlistPanel';
import SmallGroupSlotManageDialog from '../../components/admin/lane/SmallGroupSlotManageDialog';
import ManualDiscountDialog from '../../components/admin/ManualDiscountDialog';
import NetAmount from '../../components/shared/NetAmount';
import { RowAmountFixer } from '../../components/admin/RowAmountFixer';
import {
  LANE_COLORS, DEFAULT_LANE_COLOR, formatDate, formatTime,
  parseLocalDate, toLocalDateString, timeStringToMinutes,
} from '../../components/admin/lane/utils';
import { useBusinessTimezone, getTodayInTz, parseDateOnly } from '../../utils/businessTime';

const LaneManagement = () => {
  // iter317 — Phase 7B plan gate. Pivots the "Walk-in Booking" admin
  // action to the upgrade modal when the tenant's plan lacks
  // `bookings.lanes`.
  const lanesFeature = useFeature('bookings.lanes');
  // The whole page anchors on the business timezone (configured in
  // Business Settings). Browser-local "today" would shift the calendar
  // by a day for any admin travelling outside ET.
  const businessTz = useBusinessTimezone();
  const [LANES, setLANES] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initial date uses the BUSINESS timezone "today" (not the browser's).
  // We still keep `toLocalDateString` as a transitional fallback while
  // the public TZ fetch is in flight on first paint.
  const [selectedDate, setSelectedDate] = useState(() => getTodayInTz(businessTz || 'America/New_York'));

  // Once the live business TZ resolves, snap selectedDate to that day if
  // the user hasn't navigated yet (still showing the boot "today").
  const [hasNavigated, setHasNavigated] = useState(false);
  useEffect(() => {
    if (!businessTz || hasNavigated) return;
    const businessToday = getTodayInTz(businessTz);
    setSelectedDate((prev) => (prev === businessToday ? prev : businessToday));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTz]);
  const [availability, setAvailability] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);

  // Race-condition guard for rapid date navigation — see fetchData().
  const fetchRequestId = useRef(0);
  const [classes, setClasses] = useState([]);
  const [viewMode, setViewMode] = useState('calendar');

  // Edit-series dialog (for recurring bookings).
  const [editSeriesOpen, setEditSeriesOpen] = useState(false);
  const [editSeriesBooking, setEditSeriesBooking] = useState(null);

  // Cancel-series dialog (for recurring bookings).
  const [cancelSeriesOpen, setCancelSeriesOpen] = useState(false);
  const [cancelSeriesBooking, setCancelSeriesBooking] = useState(null);
  const [cancelSeriesReason, setCancelSeriesReason] = useState('');
  const [cancelSeriesLoading, setCancelSeriesLoading] = useState(false);

  // Cancel-single dialog.
  const [cancelSingleOpen, setCancelSingleOpen] = useState(false);
  const [cancelSingleBooking, setCancelSingleBooking] = useState(null);
  const [cancelSingleReason, setCancelSingleReason] = useState('');
  const [cancelSingleLoading, setCancelSingleLoading] = useState(false);

  // Refund dialog.
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundBooking, setRefundBooking] = useState(null);

  // Block Lane modal (create + edit).
  const [showBlockModal, setShowBlockModal] = useState(false);
  // Small group session management dialog (opens when admin clicks a
  // small_group block on the calendar instead of the generic block edit modal).
  const [smallGroupDialogOpen, setSmallGroupDialogOpen] = useState(false);
  const [smallGroupSlotId, setSmallGroupSlotId] = useState(null);
  const [blockForm, setBlockForm] = useState({
    lane_id: '1', date: '', start_time: '09:00', end_time: '10:00',
    block_type: 'general', class_id: '', coach_id: '', reason: '',
    is_recurring: false, recurrence_day: '', recurrence_weeks: 12,
  });
  const [coaches, setCoaches] = useState([]);
  const [deleteBlockDialogOpen, setDeleteBlockDialogOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [deleteBlockScope, setDeleteBlockScope] = useState('this');
  const [blockToDeleteHasSeries, setBlockToDeleteHasSeries] = useState(false);

  // Edit-a-block state (reuses the Block Lane modal in edit mode).
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editingBlockHasSeries, setEditingBlockHasSeries] = useState(false);
  const [editScope, setEditScope] = useState('this');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [bookingToCancelMeta, setBookingToCancelMeta] = useState(null); // { payment_status, price }
  const [cancelRefundMode, setCancelRefundMode] = useState('auto');
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState(null);

  // Admin booking modal.
  const [showAdminBookingModal, setShowAdminBookingModal] = useState(false);
  const [adminBookingForm, setAdminBookingForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    lane_id: '1', date: '', start_time: '09:00', duration: '30', notes: '',
    equipment_rentals: [],
    terms_accepted: false,
    mark_paid: false,
    paid_payment_method: 'cash',
    paid_reference_number: '',
    paid_notes: '',
  });

  // Slot action modal.
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSlotModal, setShowSlotModal] = useState(false);

  // Booking details modal.
  const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);

  // Manual discount dialog
  const [discountBooking, setDiscountBooking] = useState(null);

  // Waitlist.
  const [waitlist, setWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  const [undoingBookingId, setUndoingBookingId] = useState(null);

  // Reschedule modal.
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedBookingToReschedule, setSelectedBookingToReschedule] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    lane_id: '1', date: '', start_time: '09:00', duration: '30',
    reason: '', notify_customer: true,
    equipment_rentals: [], notes: '',
    price_override_enabled: false, price_override: '',
    force_override: false,
  });

  // Drag and drop.
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropTargetSlot, setDropTargetSlot] = useState(null);

  // Payment modal.
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [paymentAction, setPaymentAction] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'cash', amount: '', notes: '', reference_number: '', custom_message: '',
  });
  const [processingPayment, setProcessingPayment] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // ---- Effects --------------------------------------------------------------
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success('Payment processed successfully!');
      setSearchParams({});
      fetchData();
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchClasses();
    fetchLanes();
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (viewMode === 'list') fetchWaitlist();
  }, [viewMode]);

  // Keep the BookingDetailsModal's snapshot in sync with the latest
  // bookings list. This fixes a UX bug where applying a discount or
  // adjusting the price didn't refresh the modal until it was closed
  // and reopened.
  useEffect(() => {
    if (!selectedBookingDetails) return;
    const fresh = bookings.find((b) => b.id === selectedBookingDetails.id);
    if (fresh && fresh !== selectedBookingDetails) {
      setSelectedBookingDetails(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  // ---- Data fetchers --------------------------------------------------------
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      setClasses(response.data);
    } catch (error) { console.error('Error fetching classes:', error); }
  };

  const fetchLanes = async () => {
    try {
      const response = await api.get('/lanes');
      setLANES(response.data.map((l) => ({ id: l.id, name: l.name })));
    } catch (error) { console.error('Error fetching lanes:', error); }
  };

  const fetchData = async () => {
    const requestId = ++fetchRequestId.current;
    const requestedDate = selectedDate;
    setLoading(true);
    const [availRes, blocksRes, bookingsRes, coachesRes] = await Promise.allSettled([
      api.get(`/lanes/availability?date=${requestedDate}`),
      api.get(`/lanes/admin/blocks?date=${requestedDate}`),
      // Pass the date so the backend can scope the response to a single day.
      // Without this, tenants with 1000+ future-dated recurring bookings can
      // push today's rows past the `sort('date', -1).to_list(1000)` window,
      // making them invisible on the calendar/list even though they exist
      // in Mongo. (P0 bug Feb 2026.)
      api.get(`/lanes/bookings?date=${requestedDate}`),
      api.get('/coaches'),
    ]);
    if (requestId !== fetchRequestId.current) return;

    if (availRes.status === 'fulfilled') {
      setAvailability(availRes.value.data);
    } else {
      console.warn('[LaneMgmt] availability fetch failed:', availRes.reason?.response?.data || availRes.reason?.message);
      setAvailability({ date: requestedDate, is_open: false, business_hours: {}, lanes: [] });
    }
    if (blocksRes.status === 'fulfilled') setBlocks(blocksRes.value.data); else setBlocks([]);
    if (bookingsRes.status === 'fulfilled') {
      setBookings(bookingsRes.value.data.filter((b) => b.date === requestedDate));
    } else setBookings([]);
    if (coachesRes.status === 'fulfilled') setCoaches(coachesRes.value.data);
    setLoading(false);
  };

  const fetchWaitlist = async () => {
    setLoadingWaitlist(true);
    try {
      const response = await api.get('/lanes/admin/waitlist');
      setWaitlist(response.data || []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      setWaitlist([]);
    } finally {
      setLoadingWaitlist(false);
    }
  };

  const handleRemoveFromWaitlist = async (waitlistId) => {
    try {
      await api.delete(`/lanes/admin/waitlist/${waitlistId}`);
      toast.success('Removed from waitlist');
      fetchWaitlist();
    } catch (error) { toast.error('Failed to remove from waitlist'); }
  };

  // ---- Date navigation ------------------------------------------------------
  const navigateDate = (days) => {
    setHasNavigated(true);
    const current = parseLocalDate(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(toLocalDateString(current));
  };
  const handleCalendarSelect = (date) => {
    if (date) { setHasNavigated(true); setSelectedDate(toLocalDateString(date)); setCalendarOpen(false); }
  };

  // ---- Block handlers -------------------------------------------------------
  const openBlockModal = () => {
    setBlockForm({ ...blockForm, date: selectedDate });
    setShowBlockModal(true);
  };

  const handleCreateBlock = async () => {
    try {
      let reason = blockForm.reason;
      if (blockForm.block_type === 'coaching') {
        const selectedClass = classes.find((c) => c.id === blockForm.class_id);
        const selectedCoach = coaches.find((c) => c.id === blockForm.coach_id);
        reason = `Coaching: ${selectedClass?.name || 'Class'} - ${selectedCoach?.name || 'TBD'}`;
      }

      if (editingBlockId) {
        await api.patch(`/lanes/admin/blocks/${editingBlockId}?scope=${editScope}`, {
          lane_id: blockForm.lane_id, date: blockForm.date,
          start_time: blockForm.start_time, end_time: blockForm.end_time,
          class_id: blockForm.class_id || null, reason,
        });
        toast.success(
          editScope === 'this' ? 'Block updated'
          : editScope === 'future' ? 'This and future occurrences updated'
          : 'Entire series updated'
        );
      } else if (blockForm.is_recurring && blockForm.recurrence_weeks > 1) {
        const res = await api.post('/lanes/admin/blocks/series', {
          lane_id: blockForm.lane_id, start_date: blockForm.date,
          start_time: blockForm.start_time, end_time: blockForm.end_time,
          class_id: blockForm.class_id || null, reason,
          recurrence_day: blockForm.recurrence_day || null,
          recurrence_weeks: blockForm.recurrence_weeks,
        });
        toast.success(`Created ${res.data?.length || blockForm.recurrence_weeks} coaching blocks`);
      } else {
        await api.post('/lanes/admin/blocks', {
          lane_id: blockForm.lane_id, date: blockForm.date,
          start_time: blockForm.start_time, end_time: blockForm.end_time,
          class_id: blockForm.class_id || null, reason, is_recurring: false,
        });
        toast.success('Lane blocked');
      }

      setShowBlockModal(false);
      setEditingBlockId(null);
      setEditingBlockHasSeries(false);
      setEditScope('this');
      setBlockForm({
        lane_id: '1', date: selectedDate, start_time: '09:00', end_time: '10:00',
        block_type: 'general', class_id: '', coach_id: '', reason: '',
        is_recurring: false, recurrence_day: '', recurrence_weeks: 12,
      });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save block'); }
  };

  const openEditBlockModal = (block) => {
    // Small-group blocks have their own management dialog (roster +
    // attendance + invoice generation). Generic blocks fall through to
    // the standard edit modal.
    if (block.small_group_slot_id) {
      setSmallGroupSlotId(block.small_group_slot_id);
      setSmallGroupDialogOpen(true);
      return;
    }
    const reason = block.reason || '';
    let block_type = 'general';
    let coach_id = '';
    if (reason.startsWith('Coaching:')) {
      block_type = 'coaching';
      const coachName = reason.split(' - ').slice(-1)[0]?.trim();
      if (coachName) coach_id = (coaches.find((c) => c.name === coachName) || {}).id || '';
    }
    setBlockForm({
      lane_id: block.lane_id, date: block.date,
      start_time: block.start_time, end_time: block.end_time,
      block_type, class_id: block.class_id || '', coach_id,
      reason: block_type === 'general' ? reason : '',
      is_recurring: !!block.recurrence_id,
      recurrence_day: block.recurrence_day || '', recurrence_weeks: 12,
    });
    setEditingBlockId(block.id);
    setEditingBlockHasSeries(!!block.recurrence_id);
    setEditScope('this');
    setShowBlockModal(true);
  };

  const handleDeleteBlock = (blockId) => {
    const target = blocks.find((b) => b.id === blockId);
    setBlockToDelete(blockId);
    setBlockToDeleteHasSeries(!!target?.recurrence_id);
    setDeleteBlockScope('this');
    setDeleteBlockDialogOpen(true);
  };

  const confirmDeleteBlock = async () => {
    if (!blockToDelete) return;
    try {
      await api.delete(`/lanes/admin/blocks/${blockToDelete}?scope=${deleteBlockScope}`);
      toast.success(
        deleteBlockScope === 'this' ? 'Block removed'
        : deleteBlockScope === 'future' ? 'This and future occurrences removed'
        : 'Entire series removed'
      );
      setDeleteBlockDialogOpen(false);
      setBlockToDelete(null);
      setBlockToDeleteHasSeries(false);
      setDeleteBlockScope('this');
      fetchData();
    } catch (error) { toast.error('Failed to delete block'); }
  };

  // ---- Admin booking handlers -----------------------------------------------
  const openAdminBookingModal = () => {
    // iter317 — Phase 7B plan gate. Block walk-in bookings when the
    // tenant's plan doesn't include `bookings.lanes`.
    if (!lanesFeature.enabled) {
      lanesFeature.prompt('Creating a walk-in lane booking');
      return;
    }
    // Reset terms_accepted on each open so admins must reconfirm consent
    // for every walk-in booking.
    setAdminBookingForm({
      ...adminBookingForm,
      date: selectedDate,
      terms_accepted: false,
    });
    setShowAdminBookingModal(true);
  };

  const handleAdminBooking = async () => {
    try {
      // iter307bc — When admin picks the "Charge via Stripe — save card"
      // path, create the booking WITHOUT mark_paid, then immediately
      // hit /send-payment-link which returns a Stripe Checkout URL that
      // saves the card on file (setup_future_usage=off_session). Open
      // the URL in a new tab so the staff member can complete the card
      // entry on the customer's behalf right there.
      if (adminBookingForm.charge_via_stripe_new) {
        const payload = {
          ...adminBookingForm,
          mark_paid: false,
          charge_saved_pm: false,
          saved_payment_method_id: null,
        };
        const { data: createRes } = await api.post('/lanes/admin/bookings', payload);
        const bookingId = createRes?.booking?.id || createRes?.id;
        if (!bookingId) {
          toast.error('Booking created but no ID returned. Open the row and use Send Payment Link manually.');
        } else {
          try {
            const { data: linkRes } = await api.post(
              `/lanes/admin/bookings/${bookingId}/send-payment-link`,
              {},
            );
            // Backend returns the Stripe Checkout URL as `checkout_url`
            // (see lanes_admin_bookings.send_booking_payment_link). Older
            // generic fallbacks kept for safety in case the response
            // shape ever changes.
            const url = linkRes?.checkout_url || linkRes?.payment_link_url || linkRes?.url;
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
              toast.success('Booking created. Stripe Checkout opened in a new tab — card will be saved on file once paid.');
            } else {
              toast.warning('Booking created but payment-link generation returned no URL. Use the row\'s Send Payment Link action.');
            }
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Booking created but failed to open Stripe Checkout.');
          }
        }
        setShowAdminBookingModal(false);
        fetchData();
        return;
      }

      const { data } = await api.post('/lanes/admin/bookings', adminBookingForm);
      if (data?.mark_paid_error) {
        // Booking saved but inline payment hit a snag — surface so the
        // admin can retry from the row's "Record Payment" action.
        toast.warning(data.mark_paid_error, { duration: 9000 });
      } else if (adminBookingForm.charge_saved_pm) {
        toast.success('Booking created and card charged');
      } else if (adminBookingForm.mark_paid) {
        toast.success('Booking created and marked paid');
      } else {
        toast.success('Booking created');
      }
      setShowAdminBookingModal(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create booking'); }
  };

  // ---- Payment actions ------------------------------------------------------
  const openPaymentModal = (booking, action) => {
    setSelectedBookingForPayment(booking);
    setPaymentAction(action);
    setPaymentForm({
      payment_method: 'cash', amount: booking.price?.toString() || '',
      notes: '', reference_number: '', custom_message: '',
      // Price override fields — null means "no change". Reset on every open
      // so a previous override doesn't bleed into the next booking.
      price_override: null,
      discount_amount: null,
      discount_percent: null,
      price_adjustment_reason: '',
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedBookingForPayment) return;
    setProcessingPayment(true);
    try {
      // PR 19y5 — Overpayment: cap amount at booking.price when admin
      // chose "credit the overage", pass overage_credit_amount along.
      const bookingPrice = Number(selectedBookingForPayment?.price || 0);
      const entered = paymentForm.amount ? parseFloat(paymentForm.amount) : bookingPrice;
      const isOffline = !['card', 'account_credit'].includes(paymentForm.payment_method);
      const overage = isOffline && entered > bookingPrice + 0.005
        ? +(entered - bookingPrice).toFixed(2)
        : 0;
      const wantCredit = paymentForm.overage_action !== 'ignore';
      const effectiveAmount = overage > 0 ? bookingPrice : entered;
      const overageCreditAmount = overage > 0 && wantCredit ? overage : 0;
      const response = await api.post(`/lanes/admin/bookings/${selectedBookingForPayment.id}/record-payment`, {
        payment_method: paymentForm.payment_method,
        amount: effectiveAmount,
        notes: paymentForm.notes || null,
        reference_number: paymentForm.reference_number || null,
        overage_credit_amount: overageCreditAmount,
      });
      const credited = Number(response.data?.overage_credited || 0);
      if (credited > 0) {
        toast.success(`Payment recorded — ${cs()}${credited.toFixed(2)} overage credited to customer's account`);
      } else {
        toast.success(response.data.message);
      }
      if (response.data.email_sent) toast.info(`Invoice sent to customer (${response.data.invoice_number})`);
      setShowPaymentModal(false);
      setSelectedBookingForPayment(null);
      setShowBookingDetailsModal(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record payment'); }
    finally { setProcessingPayment(false); }
  };

  const handleSendPaymentLink = async () => {
    if (!selectedBookingForPayment) return;
    setProcessingPayment(true);
    try {
      const response = await api.post(`/lanes/admin/bookings/${selectedBookingForPayment.id}/send-payment-link`, {
        send_email: true,
        custom_message: paymentForm.custom_message || null,
        // Pass through any optional price adjustment. Backend treats
        // null fields as "no change", so this is safe even when admin
        // didn't open the override section.
        price_override: paymentForm.price_override ?? null,
        discount_amount: paymentForm.discount_amount ?? null,
        discount_percent: paymentForm.discount_percent ?? null,
        price_adjustment_reason: paymentForm.price_adjustment_reason || null,
      });
      toast.success('Payment link created!');
      if (response.data.email_sent) toast.info('Payment link sent to customer email');
      if (response.data.checkout_url) {
        navigator.clipboard.writeText(response.data.checkout_url);
        toast.success('Payment link copied to clipboard');
      }
      setShowPaymentModal(false);
      setSelectedBookingForPayment(null);
      setShowBookingDetailsModal(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send payment link'); }
    finally { setProcessingPayment(false); }
  };

  const handleProcessCardPayment = async () => {
    if (!selectedBookingForPayment) return;
    setProcessingPayment(true);
    try {
      const response = await api.post(`/lanes/admin/bookings/${selectedBookingForPayment.id}/process-card-payment`);
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process card payment');
      setProcessingPayment(false);
    }
  };

  const handleDownloadInvoice = async (bookingId) => {
    try {
      const response = await api.get(`/lanes/admin/bookings/${bookingId}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (error) { toast.error('Failed to download invoice'); }
  };

  const handleEmailInvoice = async (bookingId) => {
    try {
      const response = await api.post(`/lanes/admin/bookings/${bookingId}/email-invoice`);
      toast.success(`Invoice emailed to ${response.data.recipient}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to email invoice'); }
  };

  // ---- Reschedule -----------------------------------------------------------
  const openRescheduleModal = (booking) => {
    setSelectedBookingToReschedule(booking);
    // Strip equipment lines down to {equipment_id, quantity} for the picker —
    // it will re-resolve subtotals on the fly. Notes & price_override default
    // to OFF so a quick reschedule doesn't accidentally overwrite the price.
    const equipment_rentals = (booking.equipment_rentals || []).map((l) => ({
      equipment_id: l.equipment_id,
      quantity: l.quantity,
      equipment_name: l.equipment_name,
      subtotal: l.subtotal,
    }));
    setRescheduleForm({
      lane_id: booking.lane_id, date: booking.date, start_time: booking.start_time,
      duration: String(booking.duration || 30), reason: '', notify_customer: true,
      equipment_rentals,
      notes: booking.notes || '',
      price_override_enabled: false,
      price_override: '',
      force_override: false,
    });
    setShowRescheduleModal(true);
  };

  const handleReschedule = async () => {
    if (!selectedBookingToReschedule) return;
    try {
      const payload = {
        lane_id: rescheduleForm.lane_id,
        date: rescheduleForm.date,
        start_time: rescheduleForm.start_time,
        duration: rescheduleForm.duration,
        reason: rescheduleForm.reason,
        notify_customer: rescheduleForm.notify_customer,
        notes: rescheduleForm.notes ?? '',
        equipment_rentals: (rescheduleForm.equipment_rentals || []).map((l) => ({
          equipment_id: l.equipment_id, quantity: l.quantity,
        })),
        force_override: !!rescheduleForm.force_override,
      };
      if (rescheduleForm.price_override_enabled
        && rescheduleForm.price_override !== ''
        && rescheduleForm.price_override !== null
        && rescheduleForm.price_override !== undefined) {
        payload.price_override = Number(rescheduleForm.price_override);
      }
      const response = await api.put(
        `/lanes/admin/bookings/${selectedBookingToReschedule.id}/reschedule`,
        payload,
      );
      toast.success('Booking updated successfully');
      const delta = Number(response.data?.price_delta || 0);
      if (Math.abs(delta) > 0.005) {
        const wasPaid = selectedBookingToReschedule.payment_status === 'paid';
        const direction = delta > 0
          ? `+${cs()}${delta.toFixed(2)} owed by customer`
          : `${cs()}${Math.abs(delta).toFixed(2)} to refund`;
        toast.info(
          wasPaid
            ? `Price changed: ${direction}. Open this booking to send link or refund.`
            : `Price changed by ${cs()}${delta.toFixed(2)}.`,
          { duration: 7000 },
        );
      }
      if (response.data.notification_sent) toast.info('Customer has been notified');
      setShowRescheduleModal(false);
      setSelectedBookingToReschedule(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update booking'); }
  };
  // Undo a manual "Record Payment" on a lane booking. Only offered for
  // rows paid via an offline method (cash/check/zelle/venmo/card/other).
  // Stripe-settled rows must use the Refund flow; the backend also
  // enforces these rules and 409s if somehow bypassed.
  const handleUndoMarkPaidBooking = async (booking) => {
    if (!window.confirm(
      `Undo manual payment for this booking? Reverts status to Unpaid and clears the payment record. The admin action will be audit-logged.`
    )) return;
    setUndoingBookingId(booking.id);
    try {
      await api.post(`/lanes/admin/bookings/${booking.id}/undo-mark-paid`);
      toast.success('Payment reversed; booking marked Unpaid');
      await fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Undo failed');
      toast.error(msg);
    } finally {
      setUndoingBookingId(null);
    }
  };

  const handleCancelBooking = (booking) => {
    const bookingId = typeof booking === 'string' ? booking : booking?.id;
    const isPaid = typeof booking === 'object' && booking !== null
      ? booking.payment_status === 'paid'
      : false;
    setBookingToCancel(bookingId);
    setBookingToCancelMeta({
      payment_status: typeof booking === 'object' ? booking?.payment_status : undefined,
      price: typeof booking === 'object' ? booking?.price : undefined,
    });
    // For unpaid bookings, refund mode is irrelevant — send 'no_refund' (nothing
    // to refund) and skip rendering the picker entirely.
    setCancelRefundMode(isPaid ? 'auto' : 'no_refund');
    setCancelBookingDialogOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;
    try {
      await api.post(`/lanes/bookings/${bookingToCancel}/cancel`, {
        reason: 'Cancelled by admin',
        refund_mode: cancelRefundMode,
      });
      const labels = {
        auto: 'Booking cancelled (per policy)',
        full_refund: 'Booking cancelled — full refund issued',
        credit: 'Booking cancelled — account credit issued',
        no_refund: 'Booking cancelled — payment forfeited',
      };
      toast.success(labels[cancelRefundMode] || 'Booking cancelled');
      setCancelBookingDialogOpen(false);
      setBookingToCancel(null);
      setBookingToCancelMeta(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel booking'); }
  };

  // ---- Drag-and-drop --------------------------------------------------------
  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.id);
    e.dataTransfer.setData('application/json', JSON.stringify(booking));
  };
  const handleDragEnd = () => { setDraggedBooking(null); setIsDragging(false); setDropTargetSlot(null); };
  const handleDragOver = (e, laneId, timeSlot, isAvailable) => {
    e.preventDefault(); e.stopPropagation();
    if (isAvailable) { e.dataTransfer.dropEffect = 'move'; setDropTargetSlot({ laneId, timeSlot }); }
    else { e.dataTransfer.dropEffect = 'none'; }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) setDropTargetSlot(null);
  };
  const handleDrop = async (e, laneId, timeSlot) => {
    e.preventDefault(); e.stopPropagation();
    setDropTargetSlot(null);
    let booking = draggedBooking;
    if (!booking) {
      try {
        const bookingJson = e.dataTransfer.getData('application/json');
        if (bookingJson) booking = JSON.parse(bookingJson);
      } catch (err) { console.error('Failed to parse booking data:', err); }
    }
    if (!booking) {
      const bookingId = e.dataTransfer.getData('text/plain');
      if (bookingId) booking = bookings.find((b) => b.id === bookingId);
    }
    if (!booking) {
      toast.error('Could not identify booking to reschedule');
      handleDragEnd();
      return;
    }
    setRescheduleData({ booking, laneId, timeSlot });
    setRescheduleDialogOpen(true);
  };

  const confirmReschedule = async () => {
    if (!rescheduleData) return;
    const { booking, laneId, timeSlot } = rescheduleData;
    try {
      await api.put(`/lanes/admin/bookings/${booking.id}/reschedule`, {
        lane_id: laneId, date: selectedDate, start_time: timeSlot,
        duration: String(booking.duration || 30),
        reason: 'Admin rescheduled via drag-and-drop', notify_customer: true,
      });
      toast.success('Booking rescheduled successfully! Customer notified.');
      setRescheduleDialogOpen(false);
      setRescheduleData(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reschedule booking'); }
    finally { handleDragEnd(); }
  };

  const cancelReschedule = () => {
    setRescheduleDialogOpen(false);
    setRescheduleData(null);
    handleDragEnd();
  };

  // iter330 — Legacy lane fallback. Production case: booking
  // `5786d4fe-…` with `lane_id: "2"` was invisible because the
  // tenant's active lane list uses different IDs (either UUIDs or
  // simply doesn't include a lane with id="2"). We MUST render every
  // confirmed booking somewhere on the calendar; silently dropping a
  // paid reservation is a data-integrity bug.
  //
  // Resolution policy (in order):
  //   1. If the raw `booking.lane_id` matches an active lane's id,
  //      render it in that column (unchanged behaviour).
  //   2. Else, try to map the numeric id to a lane whose display
  //      name contains that same number (e.g. `"Lane 2"` for id `"2"`).
  //      This adopts legacy numeric bookings into the equivalent
  //      renamed/reid'd lane without staff intervention.
  //   3. Otherwise, append a virtual `Legacy lane N` column to the
  //      grid so the booking is still visible AND still eligible for
  //      the refund/cancel/conflict-resolver flows.
  //
  // iter330b — normalization must run even when LANES is empty (e.g.
  // brand-new tenant with no lane_config rows yet, or an outage on
  // GET /lanes). Otherwise an orphan booking on such a day would
  // still vanish. The empty-LANES branch just falls straight into
  // the virtual-column path.
  const { augmentedLanes, effectiveBookings } = useMemo(() => {
    const lanes = LANES || [];
    const laneById = new Map(lanes.map((l) => [String(l.id), l]));
    const laneByNumber = new Map();
    lanes.forEach((l) => {
      const m = String(l.name || '').match(/(\d+)/);
      if (m && !laneByNumber.has(m[1])) laneByNumber.set(m[1], l);
    });

    const virtualNeeded = new Map();
    const normalized = (bookings || []).map((b) => {
      const raw = b.lane_id == null ? '' : String(b.lane_id);
      if (laneById.has(raw)) {
        return { ...b, _effective_lane_id: raw };
      }
      if (/^\d+$/.test(raw) && laneByNumber.has(raw)) {
        const mapped = laneByNumber.get(raw);
        return {
          ...b,
          _effective_lane_id: mapped.id,
          _remapped_from_lane_id: raw,
        };
      }
      const virtId = `__legacy__:${raw || 'unknown'}`;
      if (!virtualNeeded.has(virtId)) {
        virtualNeeded.set(virtId, {
          id: virtId,
          name: raw ? `Legacy lane ${raw}` : 'Unassigned lane',
          _legacy: true,
        });
      }
      return {
        ...b,
        _effective_lane_id: virtId,
        _legacy_lane: true,
        _legacy_lane_raw_id: raw,
      };
    });
    return {
      augmentedLanes: [...lanes, ...Array.from(virtualNeeded.values())],
      effectiveBookings: normalized,
    };
  }, [LANES, bookings]);

  // iter330b — Global per-booking conflict lookup. `getSlotSpanInfo`
  // is called once per (lane, slot) and only sees bookings touching
  // THAT slot, so it can't tell a 17:00-18:00 booking that a
  // 17:30-18:30 booking overlaps it. Precomputing conflicts per
  // booking here makes the calendar badge and conflict-resolver
  // available on every rendered card of an overlapping pair,
  // regardless of which slot's cell rendered it.
  const bookingConflictMap = useMemo(() => {
    const map = new Map();
    const active = effectiveBookings.filter((b) => b.status !== 'cancelled');
    for (let i = 0; i < active.length; i += 1) {
      const a = active[i];
      const aStart = timeStringToMinutes(a.start_time);
      const aEnd = timeStringToMinutes(a.end_time);
      for (let j = i + 1; j < active.length; j += 1) {
        const b = active[j];
        if (a._effective_lane_id !== b._effective_lane_id) continue;
        const bStart = timeStringToMinutes(b.start_time);
        const bEnd = timeStringToMinutes(b.end_time);
        if (aStart < bEnd && bStart < aEnd) {
          if (!map.has(a.id)) map.set(a.id, []);
          if (!map.has(b.id)) map.set(b.id, []);
          map.get(a.id).push(b);
          map.get(b.id).push(a);
        }
      }
    }
    return map;
  }, [effectiveBookings]);

  // ---- Slot status helpers --------------------------------------------------
  const isWithinBusinessHours = (timeSlot) => {
    if (!availability?.is_open) return false;
    const openTime = availability?.business_hours?.open_time || '06:00';
    const closeTime = availability?.business_hours?.close_time || '23:00';
    const slotMin = timeStringToMinutes(timeSlot);
    return slotMin >= timeStringToMinutes(openTime) && slotMin < timeStringToMinutes(closeTime);
  };

  const getSlotSpanInfo = (laneId, timeSlot) => {
    const slotStart = timeStringToMinutes(timeSlot);
    // iter329 — Collect ALL bookings/blocks touching this slot so
    // conflicts are surfaced instead of hidden. The previous first-
    // match short-circuit silently swallowed the second overlapping
    // booking on the calendar, making paid bookings appear missing
    // (the user could see them on the customer profile + in Mongo
    // but not on the admin calendar). Race-condition prevention
    // ships at the writer layer; the reader now trusts data and
    // shows everything so admins can resolve conflicts manually.
    const matchingBookings = [];
    for (const booking of effectiveBookings) {
      if (booking._effective_lane_id !== laneId || booking.status === 'cancelled') continue;
      const bStart = timeStringToMinutes(booking.start_time);
      const bEnd = timeStringToMinutes(booking.end_time);
      if (slotStart >= bStart && slotStart < bEnd) {
        matchingBookings.push({ booking, bStart, bEnd });
      }
    }
    if (matchingBookings.length > 0) {
      // iter330b — Prefer as primary a booking whose start equals
      // slotStart. Otherwise a staggered overlap (17:00-18:00 and
      // 17:30-18:30) would render only the earlier booking's card
      // and drop the later one entirely — the exact class of bug
      // this fix exists to eliminate.
      const startingHere = matchingBookings.find((m) => m.bStart === slotStart);
      const primary = startingHere
        || [...matchingBookings].sort((a, b) => (a.bStart - b.bStart) || (b.bEnd - a.bEnd))[0];
      const isStart = slotStart === primary.bStart;
      const span = Math.ceil((primary.bEnd - primary.bStart) / 30);
      // Conflicts come from the global map so every rendered card of
      // an overlapping pair carries the badge — not only the slot
      // that happens to see both bookings simultaneously.
      const globalConflicts = bookingConflictMap.get(primary.booking.id) || [];
      return isStart
        ? {
            isStart: true,
            span,
            data: primary.booking,
            type: 'booked',
            conflicts: globalConflicts,
            conflict_count: globalConflicts.length,
          }
        : {
            isStart: false,
            span: 0,
            data: primary.booking,
            type: 'booked',
            conflicts: globalConflicts,
            conflict_count: globalConflicts.length,
          };
    }
    for (const block of blocks) {
      if (block.lane_id !== laneId) continue;
      const bStart = timeStringToMinutes(block.start_time);
      const bEnd = timeStringToMinutes(block.end_time);
      if (slotStart >= bStart && slotStart < bEnd) {
        const span = Math.ceil((bEnd - bStart) / 30);
        return slotStart === bStart
          ? { isStart: true, span, data: block, type: 'blocked', conflicts: [], conflict_count: 0 }
          : { isStart: false, span: 0, data: block, type: 'blocked', conflicts: [], conflict_count: 0 };
      }
    }
    return { isStart: false, span: 1, data: null, type: null, conflicts: [], conflict_count: 0 };
  };

  const getSlotStatus = (laneId, timeSlot) => {
    const slotStart = timeStringToMinutes(timeSlot);
    const slotEnd = slotStart + 30;
    for (const block of blocks) {
      if (block.lane_id !== laneId) continue;
      const blockStart = timeStringToMinutes(block.start_time);
      const blockEnd = timeStringToMinutes(block.end_time);
      if (slotStart < blockEnd && slotEnd > blockStart) return { type: 'blocked', data: block };
    }
    for (const booking of effectiveBookings) {
      if (booking._effective_lane_id !== laneId || booking.status === 'cancelled') continue;
      const bookingStart = timeStringToMinutes(booking.start_time);
      const bookingEnd = timeStringToMinutes(booking.end_time);
      if (slotStart < bookingEnd && slotEnd > bookingStart) return { type: 'booked', data: booking };
    }
    if (!isWithinBusinessHours(timeSlot)) return { type: 'closed', data: null };
    return { type: 'available', data: null };
  };

  // iter329 — Booking conflict map. A booking is "in conflict" when
  // another same-lane, same-day, non-cancelled booking overlaps its
  // time range. This is derived data (not persisted) — race-condition
  // prevention lives at the writer; the reader surfaces overlaps so
  // admins can resolve them manually. Returned as a Set of
  // booking.id to keep the check O(1) at render time.
  //
  // iter329b — Conflict resolver modal state. Populated when the
  // admin clicks the calendar `⚠ +N` badge; shows every overlapping
  // booking with per-row Refund + Cancel actions so an admin can
  // resolve the conflict without navigating away.
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictModalBookings, setConflictModalBookings] = useState([]);

  const conflictBookingIds = useMemo(() => {
    const set = new Set();
    const active = effectiveBookings.filter((b) => b.status !== 'cancelled');
    for (let i = 0; i < active.length; i += 1) {
      const a = active[i];
      const aStart = timeStringToMinutes(a.start_time);
      const aEnd = timeStringToMinutes(a.end_time);
      for (let j = i + 1; j < active.length; j += 1) {
        const b = active[j];
        if (a._effective_lane_id !== b._effective_lane_id) continue;
        const bStart = timeStringToMinutes(b.start_time);
        const bEnd = timeStringToMinutes(b.end_time);
        // Strict overlap (touching endpoints are fine — 5-6pm and
        // 6-7pm are back-to-back, not conflicting).
        if (aStart < bEnd && bStart < aEnd) {
          set.add(a.id);
          set.add(b.id);
        }
      }
    }
    return set;
  }, [effectiveBookings]);

  const handleSlotClick = (laneId, timeSlot) => {
    const status = getSlotStatus(laneId, timeSlot);
    if (status.type === 'available' || status.type === 'closed') {
      setSelectedSlot({ laneId, timeSlot, isClosed: status.type === 'closed' });
      setAdminBookingForm({
        customer_name: '', customer_email: '', customer_phone: '',
        lane_id: laneId, date: selectedDate, start_time: timeSlot, duration: '30',
        notes: status.type === 'closed' ? 'Override: Outside business hours' : '',
        override_hours: status.type === 'closed',
        equipment_rentals: [],
        terms_accepted: false,
      });
      setShowSlotModal(true);
    }
  };

  const handleBookedSlotClick = (booking) => {
    setSelectedBookingDetails(booking);
    setShowBookingDetailsModal(true);
  };

  const handleRescheduleFromDetails = () => {
    if (selectedBookingDetails) {
      setShowBookingDetailsModal(false);
      openRescheduleModal(selectedBookingDetails);
    }
  };

  // ---- Series cancel handlers -----------------------------------------------
  const handleConfirmCancelSeries = async () => {
    if (!cancelSeriesBooking) return;
    setCancelSeriesLoading(true);
    try {
      const res = await api.post(
        `/lanes/admin/bookings/${cancelSeriesBooking.id}/cancel-series`,
        { reason: cancelSeriesReason || undefined },
      );
      toast.success(
        `Cancelled ${res.data?.cancelled_count ?? 0} occurrence${res.data?.cancelled_count === 1 ? '' : 's'} in the series`,
      );
      setCancelSeriesOpen(false);
      setCancelSeriesBooking(null);
      setCancelSeriesReason('');
      setShowBookingDetailsModal(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel series'); }
    finally { setCancelSeriesLoading(false); }
  };

  const handleConfirmCancelSingle = async () => {
    if (!cancelSingleBooking) return;
    setCancelSingleLoading(true);
    try {
      const res = await api.post(
        `/lanes/bookings/${cancelSingleBooking.id}/cancel`,
        { reason: cancelSingleReason || undefined },
      );
      const refundType = res.data?.refund_type;
      toast.success(
        refundType === 'full_refund' ? 'Booking cancelled · Full refund applied'
        : refundType === 'credit' ? 'Booking cancelled · Account credit issued'
        : 'Booking cancelled · No refund (within 6 hours)',
      );
      setCancelSingleOpen(false);
      setCancelSingleBooking(null);
      setCancelSingleReason('');
      setShowBookingDetailsModal(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel booking'); }
    finally { setCancelSingleLoading(false); }
  };

  const openRefundDialog = (booking) => { setRefundBooking(booking); setRefundOpen(true); };

  // ---- Slot-modal quick actions ---------------------------------------------
  const handleQuickBlock = () => {
    if (!selectedSlot) return;
    const [slotHour, slotMinute] = selectedSlot.timeSlot.split(':').map(Number);
    const endMinutes = slotHour * 60 + slotMinute + 30;
    const endHour = Math.floor(endMinutes / 60);
    const endMin = endMinutes % 60;
    setBlockForm({
      lane_id: selectedSlot.laneId, date: selectedDate,
      start_time: selectedSlot.timeSlot,
      end_time: `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`,
      block_type: 'general', class_id: '', coach_id: '', reason: 'Coaching',
      is_recurring: false, recurrence_day: '', recurrence_weeks: 12,
    });
    setShowSlotModal(false);
    setShowBlockModal(true);
  };

  const handleQuickBooking = () => { setShowSlotModal(false); setShowAdminBookingModal(true); };

  // ---- Quick "Reserve Small Group Session" --------------------------------
  // Opens a modal that lists baseline small-group sessions (Admin/Staff
  // configures these once under Coaching → Small Group Sessions, including
  // coach + pricing-per-hour-by-num-kids). The user picks one and we create
  // a SLOT for it pinned to this lane × date × time. The backend's
  // create-slot endpoint auto-blocks the lane when `lane_id` is passed.
  //
  // After the slot is created we chain into RegisterPlayersDialog so the
  // staff member can add registrants right away without leaving the page.
  const [showSmallGroupModal, setShowSmallGroupModal] = useState(false);
  const [registerSlot, setRegisterSlot] = useState(null);
  const handleQuickSmallGroup = () => {
    setShowSlotModal(false);
    setShowSmallGroupModal(true);
  };
  const handleSmallGroupCreated = (slot) => {
    fetchData();
    if (slot && slot.id) {
      setRegisterSlot(slot);
    }
  };

  if (loading && !availability) {
    return (
      <DashboardLayout title="Lane Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Lane Management">
      <div className="space-y-6" data-testid="lane-management-page">
        {/* Action bar */}
        <div className="flex flex-wrap gap-3 justify-between">
          <div className="flex gap-3">
            <Button onClick={openBlockModal} className="bg-red-500 hover:bg-red-600" data-testid="block-lane-btn">
              <Clock className="h-4 w-4 mr-2" />Block Lane
            </Button>
            <Button onClick={openAdminBookingModal} className="bg-green-500 hover:bg-green-600" data-testid="admin-booking-btn">
              <Plus className="h-4 w-4 mr-2" />Walk-in Booking
            </Button>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg overflow-hidden">
              <Button variant={viewMode === 'calendar' ? 'default' : 'ghost'} size="sm"
                onClick={() => setViewMode('calendar')} className="rounded-none">
                <Grid3X3 className="h-4 w-4 mr-1" />Calendar
              </Button>
              <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm"
                onClick={() => setViewMode('list')} className="rounded-none">
                <List className="h-4 w-4 mr-1" />List
              </Button>
            </div>
            <Link to="/admin/lanes/equipment">
              <Button variant="outline" size="sm" data-testid="lane-equipment-btn" className="gap-1.5">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Equipment</span>
              </Button>
            </Link>
            <Link to="/admin/lanes/settings">
              <Button variant="outline" size="sm" data-testid="lane-settings-btn">
                <Settings className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Date navigation */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button variant="outline" size="icon" onClick={() => navigateDate(-1)} data-testid="date-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-center flex items-center gap-3">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 hover:bg-slate-100" data-testid="date-picker-btn">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      <div className="text-left">
                        <h2 className="font-heading text-xl font-semibold text-slate-900">{formatDate(selectedDate)}</h2>
                        <p className="text-sm text-slate-500">
                          {availability?.is_open
                            ? `Open: ${formatTime(availability.business_hours?.open_time || '06:00')} - ${formatTime(availability.business_hours?.close_time || '22:00')}`
                            : 'Closed'}
                        </p>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <CalendarPicker mode="single" selected={parseLocalDate(selectedDate)} onSelect={handleCalendarSelect} initialFocus />
                    <div className="p-2 border-t">
                      <Button variant="outline" size="sm" className="w-full" onClick={() => handleCalendarSelect(parseDateOnly(getTodayInTz(businessTz)))}>
                        Go to Today
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Button variant="outline" size="icon" onClick={() => navigateDate(1)} data-testid="date-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Calendar view */}
        {viewMode === 'calendar' && (
          <CalendarGrid
            lanes={augmentedLanes}
            bookings={effectiveBookings}
            blocks={blocks}
            availability={availability}
            draggedBooking={draggedBooking}
            isDragging={isDragging}
            dropTargetSlot={dropTargetSlot}
            getSlotStatus={getSlotStatus}
            getSlotSpanInfo={getSlotSpanInfo}
            onSlotClick={handleSlotClick}
            onBookedSlotClick={handleBookedSlotClick}
            onConflictClick={(conflictBookings) => {
              setConflictModalBookings(conflictBookings);
              setConflictModalOpen(true);
            }}
            onEditBlock={openEditBlockModal}
            onDeleteBlock={handleDeleteBlock}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        )}

        {/* List view */}
        {viewMode === 'list' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {augmentedLanes.map((lane) => {
                const laneBlocks = blocks.filter((b) => b.lane_id === lane.id);
                const laneBookings = effectiveBookings.filter((b) => b._effective_lane_id === lane.id && b.status !== 'cancelled');
                return (
                  <Card key={lane.id} className={`border-2 ${lane._legacy ? 'bg-amber-50 border-amber-300' : (LANE_COLORS[lane.id]?.bg || DEFAULT_LANE_COLOR.bg)} ${lane._legacy ? '' : (LANE_COLORS[lane.id]?.border || DEFAULT_LANE_COLOR.border)}`} data-testid={`lane-card-${lane.id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {lane.name}
                        {lane._legacy && (
                          <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-400 text-amber-800" data-testid={`lane-card-legacy-badge-${lane.id}`}>
                            Legacy
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600"><strong>{laneBlocks.length}</strong> blocks</p>
                        <p className="text-sm text-slate-600"><strong>{laneBookings.length}</strong> bookings</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Blocks list */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-red-500" />Blocked Times (Coaching)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {blocks.length === 0 ? (
                  <p className="p-4 text-slate-500 text-center">No blocks for this date</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {blocks.map((block) => (
                      <div key={block.id} className="p-4 flex items-center justify-between" data-testid={`block-${block.id}`}>
                        <div>
                          <p className="font-medium text-slate-900 flex items-center gap-1">
                            {block.lane_name}: {formatTime(block.start_time)} - {formatTime(block.end_time)}
                            {block.recurrence_id && (<CalendarClock className="h-3.5 w-3.5 text-purple-600" aria-label="Recurring" />)}
                          </p>
                          <p className="text-sm text-slate-500">{block.reason} {block.class_name && `(${block.class_name})`}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {block.small_group_slot_id && (
                            <Button variant="ghost" size="icon" className="text-violet-600 hover:bg-violet-50"
                              onClick={() => {
                                const url = `${window.location.origin}/register/small-group/${block.small_group_slot_id}`;
                                navigator.clipboard?.writeText(url).catch(() => {});
                                toast.success('Registration link copied — share it with parents/customers');
                              }}
                              title="Copy shareable registration link"
                              data-testid={`list-reglink-block-${block.id}`}>
                              <LinkIcon className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100"
                            onClick={() => openEditBlockModal(block)} title="Edit block"
                            data-testid={`list-edit-block-${block.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50"
                            onClick={() => handleDeleteBlock(block.id)} title="Delete block"
                            data-testid={`list-delete-block-${block.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bookings list */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-500" />Customer Bookings
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {effectiveBookings.filter((b) => b.status !== 'cancelled').length === 0 ? (
                  <p className="p-4 text-slate-500 text-center">No bookings for this date</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {effectiveBookings.filter((b) => b.status !== 'cancelled').map((booking) => (
                      <div
                        key={booking.id}
                        className={`p-4 flex items-center justify-between ${conflictBookingIds.has(booking.id) ? 'bg-red-50 border-l-4 border-red-500' : ''}`}
                        data-testid={`booking-admin-${booking.id}`}
                      >
                        <div>
                          <p className="font-medium text-slate-900 flex items-center gap-2">
                            {booking._legacy_lane
                              ? `Legacy lane ${booking._legacy_lane_raw_id || '?'}`
                              : booking.lane_name}
                            : {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            {booking._legacy_lane && (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-amber-100 border-amber-400 text-amber-800"
                                title="This booking references a lane ID that no longer maps to an active lane. It's still visible so admins can refund or reassign it."
                                data-testid={`booking-legacy-badge-${booking.id}`}
                              >
                                Legacy lane
                              </Badge>
                            )}
                            {conflictBookingIds.has(booking.id) && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-red-600 rounded px-1.5 py-0.5"
                                title="This booking overlaps another booking on the same lane. Cancel one of the overlapping bookings to resolve."
                                data-testid={`booking-conflict-badge-${booking.id}`}
                              >
                                ⚠ Conflict
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-slate-600">{booking.customer_name} • {booking.customer_email}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1.5 flex-wrap">
                            <NetAmount
                              amount={booking.price}
                              refundedAmount={booking.refunded_amount}
                              refundStatus={booking.refund_status}
                              testId={`net-amount-${booking.id}`}
                            />
                            <span>•</span>
                            <span className={
                              booking.payment_status === 'paid'
                                ? 'text-green-600'
                                : booking.payment_status === 'processing'
                                  ? 'text-indigo-600'
                                  : (booking.payment_session_id ? 'text-blue-600' : 'text-yellow-600')
                            }>
                              {booking.payment_status === 'paid'
                                ? 'Paid'
                                : booking.payment_status === 'processing'
                                  ? 'Processing'
                                  : (booking.payment_session_id ? 'Payment Pending' : 'Unpaid')}
                            </span>
                            {/* Show "Fix Amount" affordance when this row is
                                paid but `payment_amount` is missing — the
                                Stripe reconcile may have marked it paid
                                without writing the captured amount. */}
                            {booking.payment_status === 'paid'
                              && (!booking.payment_amount || Number(booking.payment_amount) <= 0) && (
                              <RowAmountFixer
                                entityType="lane_booking"
                                entityId={booking.id}
                                currentAmount={booking.price}
                                onUpdated={fetchData}
                                compact
                              />
                            )}
                            {booking.rescheduled_at && (<span className="text-blue-500">(Rescheduled)</span>)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.recurrence_id && (
                            <Button variant="outline" size="sm"
                              onClick={() => {
                                setEditSeriesBooking({ ...booking, lane_name: LANES.find((l) => l.id === booking.lane_id)?.name });
                                setEditSeriesOpen(true);
                              }}
                              className="text-purple-600 border-purple-200 hover:bg-purple-50"
                              data-testid={`edit-series-admin-${booking.id}`}>
                              <CalendarClock className="h-4 w-4 mr-1" />Edit Series
                            </Button>
                          )}
                          {booking.recurrence_id && (
                            <Button variant="outline" size="sm"
                              onClick={() => {
                                setCancelSeriesBooking({ ...booking, lane_name: LANES.find((l) => l.id === booking.lane_id)?.name });
                                setCancelSeriesReason('');
                                setCancelSeriesOpen(true);
                              }}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`cancel-series-admin-${booking.id}`}>
                              <Ban className="h-4 w-4 mr-1" />Cancel Series
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => openRescheduleModal(booking)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`reschedule-${booking.id}`}>
                            <RefreshCw className="h-4 w-4 mr-1" />Reschedule
                          </Button>
                          {booking.payment_status === 'paid' && booking.refund_status !== 'fully_refunded' && (
                            <Button variant="outline" size="sm" onClick={() => openRefundDialog(booking)}
                              className="text-amber-700 border-amber-200 hover:bg-amber-50"
                              data-testid={`refund-btn-${booking.id}`}>
                              <DollarSign className="h-4 w-4 mr-1" />Refund
                            </Button>
                          )}
                          {booking.payment_status === 'paid'
                            && !booking.payment_session_id
                            && !booking.stripe_payment_intent
                            && ['cash', 'check', 'zelle', 'venmo', 'card', 'other', 'manual'].includes(String(booking.payment_method || '').toLowerCase())
                            && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUndoMarkPaidBooking(booking)}
                                disabled={undoingBookingId === booking.id}
                                className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                data-testid={`undo-mark-paid-${booking.id}`}
                              >
                                <RotateCcw className={`h-4 w-4 mr-1 ${undoingBookingId === booking.id ? 'animate-spin' : ''}`} />
                                Undo Mark Paid
                              </Button>
                            )}
                          {booking.payment_status !== 'paid' && (
                            <Button size="sm" onClick={() => openPaymentModal(booking, 'record')}
                              className="bg-green-500 hover:bg-green-600" data-testid={`mark-paid-${booking.id}`}>
                              <CreditCard className="h-4 w-4 mr-1" />Record Payment
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleCancelBooking(booking)}
                            className="text-red-500 hover:bg-red-50" data-testid={`cancel-booking-${booking.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* iter307j — Cancelled Bookings card with post-cancel
                refund/credit actions. Previously cancelled rows were
                filtered out of the visible list (line 1098) so admins
                couldn't issue a refund after the fact when the original
                cancel was done with `refund_mode: no_refund`. */}
            {bookings.filter((b) => b.status === 'cancelled').length > 0 && (
              <Card className="border border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <X className="h-5 w-5 text-red-500" />
                    Cancelled Bookings
                    <Badge variant="outline" className="ml-1 text-xs">
                      {bookings.filter((b) => b.status === 'cancelled').length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {bookings.filter((b) => b.status === 'cancelled').map((booking) => {
                      const wasPaid = booking.payment_status === 'paid' || booking.payment_status === 'refunded' || booking.payment_status === 'partial_refund';
                      const refundedAmt = Number(booking.refunded_amount || 0);
                      const paidAmt = Number(booking.paid_amount || booking.price || 0);
                      const refundable = wasPaid && refundedAmt < paidAmt;
                      return (
                        <div key={booking.id} className="p-4 flex items-center justify-between bg-red-50/30" data-testid={`cancelled-booking-${booking.id}`}>
                          <div>
                            <p className="font-medium text-slate-700 line-through decoration-red-300">
                              {booking.lane_name}: {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </p>
                            <p className="text-sm text-slate-600">{booking.customer_name} • {booking.customer_email}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Was {cs()}{paidAmt.toFixed(2)}
                              {refundedAmt > 0 && (
                                <span className="ml-2 text-purple-700">· Refunded {cs()}{refundedAmt.toFixed(2)}</span>
                              )}
                              {booking.cancelled_at && (
                                <span className="ml-2 text-slate-400">· cancelled {String(booking.cancelled_at).slice(0,10)}</span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {refundable ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openRefundDialog(booking)}
                                className="text-purple-700 border-purple-200 hover:bg-purple-50"
                                data-testid={`post-cancel-refund-${booking.id}`}
                              >
                                <DollarSign className="h-4 w-4 mr-1" /> Refund / Credit
                              </Button>
                            ) : refundedAmt >= paidAmt && refundedAmt > 0 ? (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                Fully refunded
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600 text-xs">
                                {booking.payment_status === 'unpaid' ? 'Was unpaid' : 'No refund needed'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Waitlist */}
            <WaitlistPanel
              waitlist={waitlist}
              loading={loadingWaitlist}
              onRefresh={fetchWaitlist}
              onRemove={handleRemoveFromWaitlist}
              onBookNow={(entry) => {
                setAdminBookingForm({
                  customer_name: entry.customer_name || entry.user_name || '',
                  customer_email: entry.customer_email || entry.user_email || '',
                  customer_phone: entry.customer_phone || '',
                  lane_id: entry.lane_id || '1',
                  date: entry.date || selectedDate,
                  start_time: entry.preferred_time || '17:00',
                  duration: '30',
                  notes: `From waitlist - ${entry.notes || ''}`,
                  equipment_rentals: [],
                  terms_accepted: false,
                });
                setShowAdminBookingModal(true);
              }}
            />
          </>
        )}
      </div>

      {/* Modals / Dialogs */}
      <SlotModal
        open={showSlotModal}
        onOpenChange={setShowSlotModal}
        selectedSlot={selectedSlot}
        lanes={LANES}
        onQuickBooking={handleQuickBooking}
        onQuickBlock={handleQuickBlock}
        onQuickSmallGroup={handleQuickSmallGroup}
      />

      <CreateSmallGroupModal
        open={showSmallGroupModal}
        onOpenChange={setShowSmallGroupModal}
        laneId={selectedSlot?.laneId}
        laneName={LANES.find((l) => l.id === selectedSlot?.laneId)?.name}
        date={selectedDate}
        startTime={selectedSlot?.timeSlot}
        onCreated={handleSmallGroupCreated}
      />

      <RegisterPlayersDialog
        open={!!registerSlot}
        onOpenChange={(o) => { if (!o) setRegisterSlot(null); }}
        slot={registerSlot}
        onDone={() => { setRegisterSlot(null); fetchData(); }}
      />

      <BlockLaneModal
        open={showBlockModal}
        onOpenChange={(open) => {
          setShowBlockModal(open);
          if (!open) { setEditingBlockId(null); setEditingBlockHasSeries(false); setEditScope('this'); }
        }}
        form={blockForm}
        setForm={setBlockForm}
        lanes={LANES}
        classes={classes}
        coaches={coaches}
        editingBlockId={editingBlockId}
        editingBlockHasSeries={editingBlockHasSeries}
        editScope={editScope}
        setEditScope={setEditScope}
        onSubmit={handleCreateBlock}
      />

      <AdminBookingModal
        open={showAdminBookingModal}
        onOpenChange={setShowAdminBookingModal}
        form={adminBookingForm}
        setForm={setAdminBookingForm}
        lanes={LANES}
        onSubmit={handleAdminBooking}
      />

      <RescheduleModal
        open={showRescheduleModal}
        onOpenChange={setShowRescheduleModal}
        form={rescheduleForm}
        setForm={setRescheduleForm}
        booking={selectedBookingToReschedule}
        lanes={LANES}
        onSubmit={handleReschedule}
      />

      <BookingDetailsModal
        open={showBookingDetailsModal}
        onOpenChange={setShowBookingDetailsModal}
        booking={selectedBookingDetails}
        lanes={LANES}
        onOpenPayment={openPaymentModal}
        onDownloadInvoice={handleDownloadInvoice}
        onEmailInvoice={handleEmailInvoice}
        onOpenEditSeries={(bk) => { setEditSeriesBooking(bk); setEditSeriesOpen(true); setShowBookingDetailsModal(false); }}
        onOpenCancelSeries={(bk) => { setCancelSeriesBooking(bk); setCancelSeriesReason(''); setCancelSeriesOpen(true); }}
        onOpenCancelSingle={(bk) => { setCancelSingleBooking(bk); setCancelSingleReason(''); setCancelSingleOpen(true); }}
        onOpenRefund={openRefundDialog}
        onOpenDiscount={(bk) => { setDiscountBooking(bk); }}
        onReschedule={handleRescheduleFromDetails}
      />

      <ManualDiscountDialog
        open={!!discountBooking}
        onOpenChange={(v) => !v && setDiscountBooking(null)}
        transactionType="lane_booking"
        transactionId={discountBooking?.id}
        onChange={() => { fetchData(); }}
      />

      <PaymentActionsModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        action={paymentAction}
        booking={selectedBookingForPayment}
        form={paymentForm}
        setForm={setPaymentForm}
        processing={processingPayment}
        onRecord={handleRecordPayment}
        onSendLink={handleSendPaymentLink}
        onProcessCard={handleProcessCardPayment}
      />

      {/* Delete-block confirmation */}
      <AlertDialog open={deleteBlockDialogOpen} onOpenChange={setDeleteBlockDialogOpen}>
        <AlertDialogContent data-testid="delete-block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Block</AlertDialogTitle>
            <AlertDialogDescription>
              {blockToDeleteHasSeries
                ? 'This block is part of a recurring series. Choose what to remove:'
                : 'Are you sure you want to remove this lane block? This will make the time slot available for booking.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {blockToDeleteHasSeries && (
            <div className="space-y-2 rounded-md bg-purple-50/60 border border-purple-200 px-3 py-2.5 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="deleteScope" value="this" checked={deleteBlockScope === 'this'} onChange={() => setDeleteBlockScope('this')} data-testid="delete-scope-this" />
                <span>Only this occurrence</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="deleteScope" value="future" checked={deleteBlockScope === 'future'} onChange={() => setDeleteBlockScope('future')} data-testid="delete-scope-future" />
                <span>This and all future occurrences</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="deleteScope" value="all" checked={deleteBlockScope === 'all'} onChange={() => setDeleteBlockScope('all')} data-testid="delete-scope-all" />
                <span>All occurrences in the series</span>
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-block-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteBlock}
              className="bg-rose-500 hover:bg-rose-600"
              data-testid="delete-block-confirm"
            >
              {blockToDeleteHasSeries
                ? (deleteBlockScope === 'this' ? 'Remove This Occurrence' : deleteBlockScope === 'future' ? 'Remove This & Future' : 'Remove Entire Series')
                : 'Remove Block'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* iter329b — Conflict Resolver Modal.
          Opens when the admin clicks the calendar `⚠ +N` badge.
          Lists every booking on the overlapping slot with a per-row
          Refund + Cancel button that opens the existing RefundDialog
          — reusing the audited refund/cancel path so the resolver
          adds visibility, not a new financial code path. */}
      <Dialog open={conflictModalOpen} onOpenChange={setConflictModalOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="conflict-resolver-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <span className="text-red-600">⚠</span>
              Lane booking conflict —
              {' '}
              {conflictModalBookings[0]?.lane_name || 'unknown lane'}
              {' '}
              on {conflictModalBookings[0]?.date}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pt-2">
            <p className="text-sm text-slate-600">
              {conflictModalBookings.length} bookings share this slot.
              Refund + cancel any that shouldn't be here; the survivor
              stays visible on the calendar.
            </p>
            {conflictModalBookings.map((b) => (
              <div
                key={b.id}
                className="flex items-start justify-between gap-3 p-3 border rounded bg-red-50 border-red-200"
                data-testid={`conflict-row-${b.id}`}
              >
                <div className="text-sm flex-1">
                  <div className="font-semibold text-slate-900">
                    {b.customer_name || 'Unknown customer'}
                    <span className="text-xs font-normal text-slate-600 ml-2">
                      {formatTime(b.start_time)} – {formatTime(b.end_time)}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Booking ID: <span className="font-mono">{b.id}</span>
                    {' · '}
                    {b.payment_status === 'paid'
                      ? <span className="text-emerald-700">Paid {cs()}{(b.total_price || b.price || 0)}</span>
                      : <span className="text-amber-700">Unpaid</span>}
                    {b.recurrence_id && <span className="ml-1 text-purple-700">· Recurring</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-700 border-red-300 hover:bg-red-100"
                  onClick={() => {
                    setConflictModalOpen(false);
                    openRefundDialog(b);
                  }}
                  data-testid={`conflict-refund-cancel-${b.id}`}
                >
                  Refund + Cancel
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflictModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking confirmation */}
      <AlertDialog open={cancelBookingDialogOpen} onOpenChange={setCancelBookingDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              {bookingToCancelMeta?.payment_status === 'paid'
                ? 'The customer will be notified. Choose how you want to handle the payment below.'
                : 'This booking has not been paid — no refund is required. The customer will be notified.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {bookingToCancelMeta?.payment_status === 'paid' ? (
            <div className="py-2 space-y-2" data-testid="cancel-refund-mode-picker">
              {[
                { value: 'auto', title: 'Auto (per policy)', hint: '≥24h → full refund · 6–24h → credit · <6h → forfeit' },
                { value: 'full_refund', title: 'Full refund', hint: 'Refund the customer back to their original payment method' },
                { value: 'credit', title: 'Account credit', hint: 'Move the paid amount to their in-app credit balance for future bookings' },
                { value: 'no_refund', title: 'Forfeit (no refund)', hint: 'Cancel without refund or credit. Customer will see no money returned.' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2 p-2 border rounded cursor-pointer ${
                    cancelRefundMode === opt.value ? 'border-rose-400 bg-rose-50/40' : 'border-slate-200'
                  }`}
                  data-testid={`cancel-refund-option-${opt.value}`}
                >
                  <input
                    type="radio"
                    className="mt-1"
                    name="cancel-refund-mode"
                    value={opt.value}
                    checked={cancelRefundMode === opt.value}
                    onChange={() => setCancelRefundMode(opt.value)}
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{opt.title}</div>
                    <div className="text-xs text-slate-500">{opt.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          ) : null}

          {/* Explicit resulting action on the invoice, so admins know what
              will happen before confirming. */}
          {(() => {
            const price = Number(bookingToCancelMeta?.price || 0);
            const amt = price > 0 ? `${cs()}${price.toFixed(2)}` : 'the payment';
            const isPaid = bookingToCancelMeta?.payment_status === 'paid';
            let label, detail, tone;
            if (!isPaid) {
              label = 'Invoice will be VOIDED';
              detail = `This booking is unpaid, so its invoice (${amt}) will be voided — nothing will be owed.`;
              tone = 'bg-slate-50 border-slate-200 text-slate-700';
            } else if (cancelRefundMode === 'full_refund') {
              label = 'Full REFUND';
              detail = `${amt} will be refunded to the customer's original payment method.`;
              tone = 'bg-emerald-50 border-emerald-200 text-emerald-800';
            } else if (cancelRefundMode === 'credit') {
              label = 'ACCOUNT CREDIT';
              detail = `${amt} will be added to the customer's in-app account credit for future bookings.`;
              tone = 'bg-blue-50 border-blue-200 text-blue-800';
            } else if (cancelRefundMode === 'no_refund') {
              label = 'Payment FORFEITED';
              detail = `${amt} will be kept — the invoice stays paid and no money is returned.`;
              tone = 'bg-amber-50 border-amber-200 text-amber-800';
            } else {
              label = 'Per policy (auto)';
              detail = `${amt} will be fully refunded (≥24h before start), credited (6–24h), or forfeited (<6h) based on timing.`;
              tone = 'bg-violet-50 border-violet-200 text-violet-800';
            }
            return (
              <div className={`rounded-lg border px-3 py-2.5 text-sm ${tone}`} data-testid="cancel-result-summary">
                <div className="font-semibold">Result: {label}</div>
                <div className="text-xs mt-0.5 opacity-90">{detail}</div>
              </div>
            );
          })()}

          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancelBooking}
              className="bg-rose-500 hover:bg-rose-600"
              data-testid="cancel-booking-confirm"
            >
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drag-drop reschedule confirmation */}
      <AlertDialog open={rescheduleDialogOpen} onOpenChange={(open) => { if (!open) cancelReschedule(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reschedule Booking</AlertDialogTitle>
            <AlertDialogDescription>
              {rescheduleData && (
                <>Reschedule {rescheduleData.booking.customer_name}'s booking from {formatTime(rescheduleData.booking.start_time)} to {formatTime(rescheduleData.timeSlot)} on Lane {rescheduleData.laneId}?</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelReschedule}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReschedule} className="bg-blue-500 hover:bg-blue-600">
              Reschedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditSeriesDialog
        open={editSeriesOpen}
        onOpenChange={setEditSeriesOpen}
        pivotBooking={editSeriesBooking}
        lanes={LANES}
        onSaved={() => fetchData()}
      />

      {/* Cancel Series */}
      <AlertDialog open={cancelSeriesOpen} onOpenChange={setCancelSeriesOpen}>
        <AlertDialogContent data-testid="cancel-series-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-600" />
              Cancel recurring series?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel <strong>this booking and every future occurrence</strong> in the
              series (same day/time, same customer). Past and already-cancelled bookings are
              untouched. No automatic refunds are issued — handle refunds separately if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {cancelSeriesBooking && (
            <div className="text-xs bg-slate-50 border rounded-md p-3 space-y-0.5">
              <div><strong>From:</strong> {cancelSeriesBooking.date} · {cancelSeriesBooking.start_time}</div>
              <div><strong>Customer:</strong> {cancelSeriesBooking.customer_name}</div>
              <div><strong>Lane:</strong> {cancelSeriesBooking.lane_name}</div>
            </div>
          )}
          <div className="space-y-1.5 pt-2">
            <Label htmlFor="cancel-series-reason" className="text-sm">Reason (optional)</Label>
            <Input
              id="cancel-series-reason"
              placeholder="e.g. Customer moved / court closure"
              value={cancelSeriesReason}
              onChange={(e) => setCancelSeriesReason(e.target.value)}
              maxLength={300}
              data-testid="cancel-series-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSeriesLoading}>Keep series</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancelSeries}
              disabled={cancelSeriesLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-series-btn"
            >
              {cancelSeriesLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {cancelSeriesLoading ? 'Cancelling…' : 'Cancel series'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel single */}
      <AlertDialog open={cancelSingleOpen} onOpenChange={setCancelSingleOpen}>
        <AlertDialogContent data-testid="cancel-single-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Cancel this booking?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cancels <strong>only this occurrence</strong>. Cancellation policy applies:
              full refund 24+ hrs before, account credit 6–24 hrs, no refund within 6 hrs.
              {cancelSingleBooking?.recurrence_id && (
                <span className="block mt-2 text-purple-600">
                  This is part of a recurring series — future occurrences will remain scheduled.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {cancelSingleBooking && (
            <div className="text-xs bg-slate-50 border rounded-md p-3 space-y-0.5">
              <div><strong>Date:</strong> {cancelSingleBooking.date} · {cancelSingleBooking.start_time}</div>
              <div><strong>Customer:</strong> {cancelSingleBooking.customer_name}</div>
              <div><strong>Lane:</strong> {cancelSingleBooking.lane_name}</div>
            </div>
          )}
          <div className="space-y-1.5 pt-2">
            <Label htmlFor="cancel-single-reason" className="text-sm">Reason (optional)</Label>
            <Input
              id="cancel-single-reason"
              placeholder="e.g. Customer requested change"
              value={cancelSingleReason}
              onChange={(e) => setCancelSingleReason(e.target.value)}
              maxLength={300}
              data-testid="cancel-single-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSingleLoading}>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancelSingle}
              disabled={cancelSingleLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-single-btn"
            >
              {cancelSingleLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {cancelSingleLoading ? 'Cancelling…' : 'Cancel booking'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Refund dialog — reusable component, backed by unified /admin/refunds */}
      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        entityType="lane_booking"
        entity={refundBooking}
        priceField="price"
        summaryLines={
          refundBooking
            ? [
                { label: 'Customer', value: refundBooking.customer_name || '—' },
                {
                  label: 'Slot',
                  value: `${refundBooking.lane_name || `Lane ${refundBooking.lane_id}`} · ${refundBooking.date} ${refundBooking.start_time}`,
                },
              ]
            : []
        }
        allowCancelCheckbox
        onRefunded={() => { setShowBookingDetailsModal(false); fetchData(); }}
      />

      {/* Small-group session manage dialog (roster, attendance, invoice gen) */}
      <SmallGroupSlotManageDialog
        open={smallGroupDialogOpen}
        onOpenChange={setSmallGroupDialogOpen}
        slotId={smallGroupSlotId}
        userRole="admin"
        onAfterChange={() => fetchData()}
      />
    </DashboardLayout>
  );
};

export default LaneManagement;
